import { useState, useEffect, useMemo, useCallback } from 'react';
import _debounce from 'lodash/debounce';
import {
  CircularProgress,
  Box,
  TextField,
  TextButton,
  SkipLink,
  Tab,
  TabContext,
  TabList,
  TabPanel,
  Typography,
  useMediaQuery,
  styled,
  classes,
} from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';
import axios from 'helpers/axios';
import { TAXONOMYCODES, TAXONOMYCODES_ID_CHILDREN } from 'constants/apis';
import { TAXONOMY_SETS } from 'constants/taxonomySets';
import { Belt, TaxonomyCodeListItem } from 'components';

const StyledTab = styled(Tab)(({ theme }) => ({
  [`&.${classes.components.MuiButtonBase.root}`]: {
    [`&.${classes.components.MuiTab.root}`]: {
      [`&.${classes.states.MuiSelected}`]: {
        borderColor: theme.palette.primary.main,
      },
      [`& .${classes.components.MuiTypography.root}`]: {
        color: theme.palette.brand.grey.charcoal,
        margin: 0,
        lineHeight: '1.5',
        textAlign: 'left',
      },
      '&:not(:last-child)': {
        margin: 0,
        [theme.breakpoints.down('md')]: {
          marginBottom: theme.spacing(3),
        },
        [theme.breakpoints.up('md')]: {
          marginRight: theme.spacing(3),
        },
      },
      flex: '1 1 auto',
      alignItems: 'flex-start',
      flexDirection: 'column',
      boxShadow: 'none',
      padding: '1.25rem 1.5rem',
    },
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  borderColor: theme.palette.brand.grey.lightGrey,
  borderWidth: '3px',
  borderStyle: 'solid',
  background: theme.palette.common.white,
  borderRadius: 8,
  outline: 'none',
}));

const StyledTabList = styled(TabList)(({ theme }) => ({
  [`& .${classes.components.MuiTabs.indicator}`]: {
    display: 'none',
  },
  [`& .${classes.components.MuiTabs.flexContainer}`]: {
    padding: theme.spacing(1),
  },
}));

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100px',
}));

const TaxonomyCodePickerTab = ({ title, description, numOfSelected, ...rest }) => (
  <StyledTab
    label={
      <>
        <Typography component="span" variant="subtitle1">
          {title}
        </Typography>
        <Typography component="span" variant="body2">
          {description}
        </Typography>
        <Typography component="span" variant="body3" sx={{ fontWeight: 'bold' }}>
          {numOfSelected}
        </Typography>
      </>
    }
    wrapped
    {...rest}
  />
);

/*
 *  Get codes based on selected parent or search terms
 */
const fetchTaxonomyCodes = async ({
  taxonomySetID = TAXONOMY_SETS.UNSPSC,
  parentTaxonomyCodeID,
  searchTerm = '',
}) => {
  if (!fetchTaxonomyCodes.cache) {
    fetchTaxonomyCodes.cache = {
      [TAXONOMY_SETS.UNSPSC]: { parentTaxonomyCodeIDs: {}, searchTerms: {} },
      [TAXONOMY_SETS.NAICS]: { parentTaxonomyCodeIDs: {}, searchTerms: {} },
    };
  }

  // we are fetching the children of a given taxonomy code
  if (parentTaxonomyCodeID) {
    if (!fetchTaxonomyCodes.cache[taxonomySetID].parentTaxonomyCodeIDs[parentTaxonomyCodeID]) {
      const url = TAXONOMYCODES_ID_CHILDREN(parentTaxonomyCodeID);
      const { data } = await axios.get(url);
      fetchTaxonomyCodes.cache[taxonomySetID].parentTaxonomyCodeIDs[parentTaxonomyCodeID] = data;
    }
    return fetchTaxonomyCodes.cache[taxonomySetID].parentTaxonomyCodeIDs[parentTaxonomyCodeID];
  }

  // we are fetching taxonomy codes based on a search term, or just the top level set of codes
  if (!fetchTaxonomyCodes.cache[taxonomySetID].searchTerms[searchTerm]) {
    const options = { params: { taxonomySetID } };
    const url = TAXONOMYCODES;
    if (searchTerm === '') {
      options.params.token2 = '';
    } else {
      options.params.search = searchTerm;
    }
    const { data } = await axios.get(url, options);
    fetchTaxonomyCodes.cache[taxonomySetID].searchTerms[searchTerm] = data;
  }

  return fetchTaxonomyCodes.cache[taxonomySetID].searchTerms[searchTerm];
};

/*
 * Tool for user to select taxonomy codes
 */
const TaxonomyCodePicker = ({
  isLoading,
  selectedTaxonomyCodes,
  setIsLoading,
  setSelectedTaxonomyCodes,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const [isShowingSelections, setIsShowingSelections] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [taxonomyCodesBreadCrumbs, setTaxonomyCodesBreadCrumbs] = useState([]);
  const [taxonomyCodesToDisplay, setTaxonomyCodesToDisplay] = useState([]);
  const [selectedTaxonomySetID, setSelectedTaxonomySetID] = useState(TAXONOMY_SETS.UNSPSC);

  /*
   *  As breadcrumbs change, fetch & show the appropriate taxonomy codes
   */
  useEffect(() => {
    (async () => {
      const taxonomyCodes = await fetchTaxonomyCodes({
        taxonomySetID: selectedTaxonomySetID,
        parentTaxonomyCodeID: taxonomyCodesBreadCrumbs.length
          ? taxonomyCodesBreadCrumbs[taxonomyCodesBreadCrumbs.length - 1].taxonomyCodeID
          : null,
      });

      setTaxonomyCodesToDisplay(taxonomyCodes);

      setIsLoading(false);
    })();
  }, [setIsLoading, selectedTaxonomySetID, taxonomyCodesBreadCrumbs]);

  /*
   * Show the codes that the user has selected
   */
  const toggleShowingSelections = useCallback(() => {
    setIsShowingSelections(prevIsShowingSelections => !prevIsShowingSelections);
    setSearchQuery('');
    setTaxonomyCodesBreadCrumbs([]);
  }, []);

  /**
   * Handle changing the current taxonomy set.
   */
  const handleTaxonomySetIDChange = useCallback((_, taxonomySetID) => {
    setTaxonomyCodesBreadCrumbs([]);
    setSelectedTaxonomySetID(taxonomySetID);
  }, []);

  /*
   * Handle searching & showing only most recenly queried results
   */
  const debouncedSearch = useMemo(
    () =>
      _debounce(async query => {
        setIsLoading(true);
        try {
          const taxonomyCodes = await fetchTaxonomyCodes({
            taxonomySetID: selectedTaxonomySetID,
            searchTerm: query,
          });
          setTaxonomyCodesToDisplay(taxonomyCodes);
        } finally {
          setIsLoading(false);
        }
      }, 500),
    [selectedTaxonomySetID, setIsLoading]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  /*
   * Start a search based on user input
   */
  const handleChangeSearch = useCallback(
    e => {
      const { value } = e.target;

      setSearchQuery(value);

      if (value.trim() === searchQuery) {
        return;
      }

      setTaxonomyCodesBreadCrumbs([]);

      debouncedSearch(value.trim(), {
        setIsLoading,
        setDisplay: setTaxonomyCodesToDisplay,
      });
    },
    [debouncedSearch, searchQuery, setIsLoading]
  );

  const selectedTaxonomyCodesBySet = useMemo(
    () =>
      selectedTaxonomyCodes.reduce(
        (acc, code) => {
          switch (code.taxonomySetID) {
            case TAXONOMY_SETS.UNSPSC: {
              acc[TAXONOMY_SETS.UNSPSC].push(code);
              return acc;
            }
            case TAXONOMY_SETS.NAICS: {
              acc[TAXONOMY_SETS.NAICS].push(code);
              return acc;
            }
            default:
              return acc;
          }
        },
        {
          [TAXONOMY_SETS.UNSPSC]: [],
          [TAXONOMY_SETS.NAICS]: [],
        }
      ),
    [selectedTaxonomyCodes]
  );

  return (
    <TabContext value={selectedTaxonomySetID}>
      <Box>
        <StyledTabList
          onChange={handleTaxonomySetIDChange}
          variant="fullWidth"
          orientation={isMobile ? 'vertical' : 'horizontal'}
          aria-label={t('settings.commodity_codes.tabs')}
        >
          <TaxonomyCodePickerTab
            title={t('settings.commodity_codes.unspsc_title')}
            description={t('settings.commodity_codes.unspsc_description')}
            numOfSelected={t('settings.commodity_codes.selected_commodity_codes', {
              count: selectedTaxonomyCodesBySet[TAXONOMY_SETS.UNSPSC].length,
            })}
            value={TAXONOMY_SETS.UNSPSC}
          />
          <TaxonomyCodePickerTab
            title={t('settings.commodity_codes.naics_title')}
            description={t('settings.commodity_codes.naics_description')}
            numOfSelected={t('settings.commodity_codes.selected_commodity_codes', {
              count: selectedTaxonomyCodesBySet[TAXONOMY_SETS.NAICS].length,
            })}
            value={TAXONOMY_SETS.NAICS}
          />
        </StyledTabList>
      </Box>
      {Object.values(TAXONOMY_SETS).map(taxonomySetID => (
        <TabPanel key={taxonomySetID} value={taxonomySetID} sx={{ p: 1 }}>
          <Belt
            sx={{
              marginTop: 3,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
            }}
          >
            <TextField
              name="search"
              type="search"
              disabled={isShowingSelections}
              sx={{ maxWidth: { xs: 'initial', md: '400px' }, flex: 2, alignSelf: 'stretch' }}
              placeholder={t('settings.commodity_codes.search_placeholder')}
              value={searchQuery}
              onChange={handleChangeSearch}
            />
            <TextButton
              variant="text"
              onClick={toggleShowingSelections}
              label={
                isShowingSelections
                  ? t('settings.commodity_codes.hide_selections')
                  : t('settings.commodity_codes.show_selections')
              }
              size="small"
              sx={{
                maxWidth: { xs: 'initial', md: '200px' },
                width: { xs: '100%', md: 'initial' },
                flex: 1,
                fontWeight: '400',
                fontSize: '13px',
                mt: { xs: 2, md: 0 },
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            />
          </Belt>
          <SkipLink href="#taxonomy-codes-bottom">
            {t('settings.commodity_codes.skip_to_bottom')}
          </SkipLink>
          <Box mb={5} mt={3}>
            {isLoading ? (
              <StyledBox>
                <CircularProgress aria-label={t('components.loading_icon')} />
              </StyledBox>
            ) : isShowingSelections ? (
              <>
                {selectedTaxonomyCodesBySet[selectedTaxonomySetID].length === 0 && (
                  <StyledBox>
                    <TextButton
                      onClick={toggleShowingSelections}
                      label={t('settings.commodity_codes.no_selections')}
                    />
                  </StyledBox>
                )}
                {selectedTaxonomyCodesBySet[selectedTaxonomySetID].map(taxonomyCode => (
                  <TaxonomyCodeListItem
                    isShowingSelections={isShowingSelections}
                    key={taxonomyCode.taxonomyCodeID}
                    selectedTaxonomyCodes={selectedTaxonomyCodesBySet[selectedTaxonomySetID]}
                    selectedTaxonomySetID={selectedTaxonomySetID}
                    setIsLoading={setIsLoading}
                    setIsShowingSelections={setIsShowingSelections}
                    setSelectedTaxonomyCodes={setSelectedTaxonomyCodes}
                    setTaxonomyCodesBreadCrumbs={setTaxonomyCodesBreadCrumbs}
                    taxonomyCode={taxonomyCode}
                    taxonomyCodesBreadCrumbs={taxonomyCodesBreadCrumbs}
                  />
                ))}
              </>
            ) : (
              [
                ...taxonomyCodesBreadCrumbs.map(taxonomyCode => (
                  <TaxonomyCodeListItem
                    indent={taxonomyCodesBreadCrumbs.indexOf(taxonomyCode)}
                    isExpanded
                    isShowingSelections={isShowingSelections}
                    key={taxonomyCode.taxonomyCodeID}
                    selectedTaxonomyCodes={selectedTaxonomyCodesBySet[selectedTaxonomySetID]}
                    selectedTaxonomySetID={selectedTaxonomySetID}
                    setIsLoading={setIsLoading}
                    setIsShowingSelections={setIsShowingSelections}
                    setSelectedTaxonomyCodes={setSelectedTaxonomyCodes}
                    setTaxonomyCodesBreadCrumbs={setTaxonomyCodesBreadCrumbs}
                    taxonomyCode={taxonomyCode}
                    taxonomyCodesBreadCrumbs={taxonomyCodesBreadCrumbs}
                  />
                )),
                ...taxonomyCodesToDisplay.map(taxonomyCode => (
                  <TaxonomyCodeListItem
                    indent={taxonomyCodesBreadCrumbs.length}
                    isShowingSelections={isShowingSelections}
                    key={taxonomyCode.taxonomyCodeID}
                    selectedTaxonomyCodes={selectedTaxonomyCodesBySet[selectedTaxonomySetID]}
                    selectedTaxonomySetID={selectedTaxonomySetID}
                    setIsLoading={setIsLoading}
                    setIsShowingSelections={setIsShowingSelections}
                    setSelectedTaxonomyCodes={setSelectedTaxonomyCodes}
                    setTaxonomyCodesBreadCrumbs={setTaxonomyCodesBreadCrumbs}
                    taxonomyCode={taxonomyCode}
                    taxonomyCodesBreadCrumbs={taxonomyCodesBreadCrumbs}
                  />
                )),
              ]
            )}
          </Box>
        </TabPanel>
      ))}
    </TabContext>
  );
};

export default TaxonomyCodePicker;
